'use client';
import React, { useCallback, useMemo, useRef } from 'react';
import dynamic from 'next/dynamic';
import { CelebrationOutlined } from '@mui/icons-material';
import { Box, CardMedia, CardProps, Skeleton } from '@mui/material';

import { useBreakpoint, useCustomNavigate, useSearch, useTheme } from '@dh/hooks';
import { isExpanded, Project } from '@dh/types';
import {
    CardContainer,
    CardContentStyled,
    ProjectTitle,
    ProjectTitlePicture,
    ProjectTitlePicturePlaceholder,
    TitleAndBadgeRow
} from './styles';
import { LabelMedium } from '@dh/styles';
import { formatDateRange, keyboardSelect } from '@dh/utils';
import { FormattedOutput } from '../formatted-output/FormattedOutput';

import { ChipContainerProps } from '../tag-container/TagContainer';

interface ProjectCardProps extends CardProps {
    project: Project;
    alternativeDescriptionText?: string;
}

const TagContainerLoader = (
    <Box mt={1} gap={1} sx={{ display: 'flex' }}>
        {Array.from({ length: 2 }).map((_, index) => (
            <Skeleton key={index} variant='rounded' width={100} height={'10%'} animation='wave' />
        ))}
    </Box>
);

/* To avoid server from trying to access DOM element in component */
const TagContainer = dynamic<ChipContainerProps>(
    () => import('../tag-container/TagContainer').then((module) => module.TagContainer),
    {
        ssr: false,
        loading: () => {
            return TagContainerLoader;
        }
    }
);

const ProjectCardComponent = ({
    project,
    alternativeDescriptionText,
    ...props
}: ProjectCardProps) => {
    const { navigateToProjectDetails } = useCustomNavigate();
    const breakpoint = useBreakpoint();
    const { colors } = useTheme();
    const cardRef = useRef<HTMLDivElement | null>(null);
    const { onSearch } = useSearch();

    const { title, topics, titleImage, documentId, text_section_0, duration } = project;

    // Memoize tags to avoid recreating on every render
    const tags = useMemo(
        () =>
            topics.map(({ title }) => ({
                title,
                onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                    e.stopPropagation();
                    onSearch(title);
                }
            })),
        [topics, onSearch]
    );

    // Memoize navigation callback
    const goToDetails = useCallback(() => {
        navigateToProjectDetails(documentId);
    }, [navigateToProjectDetails, documentId]);

    // Memoize keyboard handler
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            keyboardSelect(event, goToDetails);
        },
        [goToDetails]
    );

    // Memoize celebration icon styles
    const celebrationIconStyles = useMemo(
        () => ({ fontSize: 110, color: colors.onPrimary }),
        [colors.onPrimary]
    );

    // Memoize date range for display
    const formattedDateRange = useMemo(
        () => duration && formatDateRange(duration.start, duration.end),
        [duration]
    );

    // Memoize rows calculation
    const tagRows = useMemo(() => (!isExpanded(breakpoint) ? 1 : undefined), [breakpoint]);

    return (
        <CardContainer
            tabIndex={0}
            ref={cardRef}
            breakpoint={breakpoint}
            onClick={goToDetails}
            onKeyDown={handleKeyDown}
            {...props}
        >
            <CardMedia sx={{ height: 173 }}>
                {titleImage ? (
                    <ProjectTitlePicture
                        src={titleImage.url}
                        alt={
                            titleImage?.description ??
                            titleImage.name ??
                            alternativeDescriptionText ??
                            'The title image of the project'
                        }
                        height={173}
                        width={360}
                    />
                ) : (
                    <ProjectTitlePicturePlaceholder>
                        <CelebrationOutlined sx={celebrationIconStyles} />
                    </ProjectTitlePicturePlaceholder>
                )}
            </CardMedia>
            <CardContentStyled>
                {duration && (
                    <LabelMedium color={colors.onSurfaceVariant}>
                        {formattedDateRange}
                    </LabelMedium>
                )}
                <TitleAndBadgeRow>
                    <ProjectTitle>{title}</ProjectTitle>
                </TitleAndBadgeRow>
                <Box mb={2}>
                    <FormattedOutput maxLength={200} replaceNewLines clampLines={3}>
                        {text_section_0}
                    </FormattedOutput>
                </Box>
                <TagContainer tags={tags} rows={tagRows} />
            </CardContentStyled>
        </CardContainer>
    );
};

ProjectCardComponent.displayName = 'ProjectCard';

export const ProjectCard = React.memo(ProjectCardComponent);
